import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../lib/authFetch';
import { localizedTitle } from '../data/mealTitleTranslations';
import { Button } from '../components/ui/button';
import { BookOpen, Users, MessageSquare, ShieldAlert, Menu, Bell, Award, ChevronRight, Calendar, MessageCircle, Heart, ChevronDown, ChevronUp, Sparkles, User, ClipboardList, Folder, LifeBuoy } from 'lucide-react';
import EmergencyButton from '../components/EmergencyButton';
import ThoughtOfTheDay from '../components/ThoughtOfTheDay';
import SobrietyClock, { CLOCK_COLOR_THEMES } from '../components/SobrietyClock';
import AvatarMenu from '../components/AvatarMenu';
import { DailyTasksDropdown, WeeklyTasksDropdown } from '../components/TaskDropdowns';
import { NotificationBadge } from '../components/NotificationSystem';
import { HeaderLanguageSelector } from '../components/LanguageSelector';
import { useBadges } from '../components/Badges';
import QuickStartGuide from '../components/QuickStartGuide';
import WeeklyBanner from '../components/WeeklyBanner';
import useTheme4A from '../components/useTheme4A';
// GameStreakWidget removed per user request — badge didn't link to
// anything and wasn't earning its place on the dashboard.
import QuickCheckinModal, { QuickCheckinCard } from '../components/QuickCheckinModal';
import CountryPrompt from '../components/CountryPrompt';
import { useQuickCheckinDue } from '../components/QuickCheckinDueHook';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function DashboardPage() {
  useTheme4A({ barHeight: 64 });
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  // Old mental-health hook retired — the new quick check-in uses its own
  // due-state and never auto-opens.  Retained the import for compatibility
  // but do not invoke it.
  const { due: checkinDue, refresh: refreshCheckinDue, hide: hideCheckinCard } = useQuickCheckinDue();
  const [checkinOpen, setCheckinOpen] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const snoozeCheckin = async () => {
    try {
      await authFetch(`${apiBase}/api/quick-checkin/snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes: 60 }),
      });
    } catch (_) { /* swallow */ }
    hideCheckinCard();
  };

  const [progress, setProgress] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState({ unread: 0, urgent: 0 });
  const [personalHistoryStatus, setPersonalHistoryStatus] = useState({ completed: false, progress: 0, missing_sections: [] });
  const [selfWorkStatus, setSelfWorkStatus] = useState({ completed: 0, total: 5, progress: 0, done: false });
  const [dietPrefsCompleted, setDietPrefsCompleted] = useState(false);
  // Recovery Room notification badge — pending favourite requests + unread DMs
  const [chatNotif, setChatNotif] = useState({ pending_favourites: 0, unread_dms: 0, total: 0 });
  const chatNotifCount = chatNotif.total || 0;
  const [tonightIdea, setTonightIdea] = useState(null);
  const [showQuickStart, setShowQuickStart] = useState(false);
  // Collapsed by default — user wants Assessment & Profile as a dropdown.
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [showWeekBanner, setShowWeekBanner] = useState(false);
  const [isLgbtqi, setIsLgbtqi] = useState(false);
  const [roomsExpanded, setRoomsExpanded] = useState(false);
  const [swReminder, setSwReminder] = useState(null);
  const { earnedBadges, loading: badgesLoading, checkAndAwardBadges } = useBadges();

  const isInTwoWeekWindow = (user?.current_week || -2) <= 0;
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'beta_tester';
  const canAccessChat = isSuperAdmin || !isInTwoWeekWindow;
  // SuperAdmin sees everything as if past the 2-week window
  const effectivelyInTwoWeekWindow = isSuperAdmin ? false : isInTwoWeekWindow;
  
  const getWeekLabel = () => {
    if (isSuperAdmin) return 'SuperAdmin';
    const week = user?.current_week || -2;
    if (week < 0) return t('dashboard.twoWeekWindow');
    if (week === 0) return `${t('dashboard.twoWeekWindow')} - Final`;
    return `${t('dashboard.week')} ${week}`;
  };

  useEffect(() => {
    fetchProgress();
    fetchNotifications();
    fetchPersonalHistoryStatus();
    fetchSelfWorkStatus();
    fetchDietPrefs();
    pickTonightIdea();
    fetchQuickStartStatus();
    fetchWeekBannerStatus();
    fetchLgbtqiAccess();
    fetchSelfWorkReminder();
    fetchChatNotifCount();
    checkAndAwardBadges();
    // Poll chat notification badge every 30s so incoming favourite requests
    // and new DMs surface without a manual refresh.
    const chatPollId = setInterval(fetchChatNotifCount, 30_000);
    return () => clearInterval(chatPollId);
  }, [checkAndAwardBadges]);

  // Re-fetch personal-history status whenever the dashboard regains visibility
  // (e.g. the user just came back from /personal-history after saving a
  // section).  Keeps the missing-sections list and reminder banner in sync
  // without forcing a full page reload.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchPersonalHistoryStatus();
        fetchSelfWorkStatus();
        fetchChatNotifCount();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/progress`);
      if (response.ok) setProgress(await response.json());
    } catch (error) { console.error('Failed to fetch progress:', error); }
  };

  const fetchNotifications = async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`),
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-count`)
      ]);
      if (notifResponse.ok) setNotifications(await notifResponse.json() || []);
      if (countResponse.ok) setUnreadCount(await countResponse.json());
    } catch (error) { console.error('Failed to fetch notifications:', error); }
  };

  const fetchPersonalHistoryStatus = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/personal-history/status`);
      if (response.ok) setPersonalHistoryStatus(await response.json());
    } catch (error) { setPersonalHistoryStatus({ completed: false, progress: 0, missing_sections: [] }); }
  };

  // Fetch overall progress for the "2 Week Window Tasks" tile — 5 sub-items:
  // Introduction (sw002), Personal History, Admission Assessment, H.O.W.
  // Commitment (sw-0001), and Stickman Assessment (sw001).
  const fetchSelfWorkStatus = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/self-work-progress`);
      if (!response.ok) return;
      const data = await response.json();
      const tracked = ['sw002', 'personal-history', 'admission-assessment', 'sw-0001', 'sw001'];
      const completedList = data.completed || [];
      const completedCount = tracked.filter(id => completedList.includes(id)).length;
      const pct = Math.round((completedCount / tracked.length) * 100);
      setSelfWorkStatus({
        completed: completedCount,
        total: tracked.length,
        progress: pct,
        done: completedCount === tracked.length,
      });
    } catch (error) { /* ignore */ }
  };

  // Pull pending-favourites + unread-DMs count for the Recovery Room badge.
  // Cheap single endpoint; polls every 30s.
  const fetchChatNotifCount = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/notifications/count`);
      if (res.ok) setChatNotif(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchDietPrefs = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/diet-preferences`);
      if (response.ok) {
        const data = await response.json();
        setDietPrefsCompleted(!!data?.completed);
      }
    } catch (error) { /* ignore */ }
  };

  const pickTonightIdea = async () => {
    try {
      const { DIET_MEALS } = await import('../data/dietMeals');
      const dinners = DIET_MEALS.filter(m => m.category === 'dinner');
      if (dinners.length) setTonightIdea(dinners[Math.floor(Math.random() * dinners.length)]);
    } catch (e) { /* ignore */ }
  };

  const fetchQuickStartStatus = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/quickstart-status`);
      if (response.ok) {
        const data = await response.json();
        if (!data.has_seen_quickstart) setShowQuickStart(true);
      }
    } catch (error) { /* non-critical: quickstart just won't show */ }
  };

  const fetchWeekBannerStatus = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/week-banner-status`);
      if (response.ok) {
        const data = await response.json();
        if (!data.seen_current) setShowWeekBanner(true);
      }
    } catch (error) { /* non-critical: week banner just won't show */ }
  };

  const fetchLgbtqiAccess = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/lgbtqi/access`);
      if (response.ok) {
        const data = await response.json();
        setIsLgbtqi(!!data.allowed);
      }
    } catch (error) { /* ignore */ }
  };

  const fetchSelfWorkReminder = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/self-work/status`);
      if (response.ok) {
        const data = await response.json();
        setSwReminder(data?.reminder || null);
      }
    } catch (error) { /* ignore */ }
  };

  const formatSobrietyDate = (dateStr) => {
    if (!dateStr) return null;
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return null; }
  };

  const sobrietyDateFormatted = formatSobrietyDate(user?.sobriety_date);
  // Tudor review 9 Oct 2026 (revised): Programme progress is the 30-week
  // programme AFTER the 2 Week Window.  2WW (weeks -2, -1) counts as 0%.
  // Week 1 is 1/30, Week 30 is 30/30 = 100%.  Weeks 29 and 30 carry no
  // self-work by design but still count towards programme progress.
  const cw = Number(user?.current_week ?? -2);
  const programmeWeek = cw > 0 ? Math.min(cw, 30) : 0;
  const pct = Math.round((programmeWeek / 30) * 100);

  return (
    <div className="min-h-screen bg-white">
      {showQuickStart && <QuickStartGuide onComplete={() => setShowQuickStart(false)} />}
      {!showQuickStart && showWeekBanner && (
        <WeeklyBanner weekNumber={user?.current_week ?? -2} onDismiss={() => setShowWeekBanner(false)} />
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-2 border-black z-40 h-16 px-4 flex items-center justify-between">
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" data-testid="axiom-logo" />
        <div className="flex items-center space-x-2">
          <HeaderLanguageSelector />
          <button onClick={() => navigate('/notifications')} className="relative p-2 hover:bg-zinc-100 rounded-full transition-colors" data-testid="notifications-btn">
            <Bell className="w-5 h-5 text-black" />
            <NotificationBadge count={unreadCount.unread} urgent={unreadCount.urgent > 0} />
          </button>
          <EmergencyButton />
          <AvatarMenu size="small" />
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-32 px-4">
        <div className="max-w-md mx-auto">
          {/* User Greeting */}
          <div className="mb-6 flex items-center space-x-4">
            <AvatarMenu size="large" />
            <div>
              <div className="inline-block px-3 py-1.5 rounded-lg mb-2 bg-white border-2 border-black">
                <p className="text-base font-bold text-black" data-testid="week-label" data-arp="this-week">{getWeekLabel()}</p>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-black">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || 'User'}
              </h1>
            </div>
          </div>

          {/* Quick check-in card: appears only when a check-in is due (Sep 2026 spec). */}
          {checkinDue && (
            <div className="mb-4">
              <QuickCheckinCard
                onOpen={() => setCheckinOpen(true)}
                onSnooze={snoozeCheckin}
              />
            </div>
          )}

          {/* "Are you at risk?" banner — surfaces the Relapse Prevention plan.
              Shown when no check-in is pending (so it does not compete with the
              check-in card) or when the user just completed one. The check-in
              itself is anonymous, so this banner is a general safety nudge,
              not tied to any stored result. */}
          {!checkinDue && (
            <button
              onClick={() => navigate('/relapse-prevention')}
              className="w-full mb-4 bg-white border-2 border-black rounded-xl p-4 text-left hover:bg-zinc-50 transition-colors"
              data-testid="dashboard-at-risk-banner"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <h3 className="text-base font-bold text-black mb-0.5">Are you at risk?</h3>
                  <p className="text-base text-black leading-tight">
                    Review your Relapse Prevention plan: HALT, triggers, early warnings and who to call.
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
              </div>
            </button>
          )}

          {/* One-time prompt to capture the user's country (powers Red / Amber / Get Help). */}
          <CountryPrompt />

          {/* 2-Week Window Profile Reminder */}
          {effectivelyInTwoWeekWindow && (
            <div className="bg-white border-2 border-black rounded-xl p-4 mb-4" data-testid="two-week-window-banner">
              <h3 className="font-bold text-black text-base mb-1">{t('dashboard.twoWeekWindow')}</h3>
              <p className="text-black text-base">Complete your Personal History assessment and familiarise yourself with the programme during this initial period.</p>
              {!personalHistoryStatus.completed && personalHistoryStatus.missing_sections?.length > 0 && (
                <div className="mt-3 bg-white border-2 border-black rounded-lg p-3" data-testid="missing-sections-list">
                  <p className="text-base font-bold text-black mb-1.5">
                    Sections still to complete ({personalHistoryStatus.missing_sections.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {personalHistoryStatus.missing_sections.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center text-sm font-semibold bg-white text-black px-2 py-0.5 rounded-full border-2 border-black"
                        data-testid={`missing-section-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!personalHistoryStatus.completed && (
                <button
                  onClick={() => navigate('/personal-history', {
                    state: { incompleteSections: personalHistoryStatus.missing_sections || [] }
                  })}
                  className="mt-3 w-full rounded-lg py-2.5 text-base font-bold border-2 border-black text-black hover:brightness-95 transition-colors"
                  style={{ backgroundColor: '#ffcd04' }}
                  data-testid="personal-history-reminder-btn"
                  data-arp="btn-primary"
                >
                  {personalHistoryStatus.progress > 0
                    ? `Continue Personal History (${personalHistoryStatus.progress}% complete)`
                    : 'Start Personal History: required before Week 1'}
                </button>
              )}
            </div>
          )}

          {/* Thought of the Day: FIRST box */}
          <div className="mb-4">
            <ThoughtOfTheDay />
          </div>

          {/* Batch 11 E5: Programme completion banner. Shown once the user
              submits the Programme Week 30 Self-Work (week=32). */}
          {user?.programme_completed_at && (
            <div
              className="mb-4 bg-white border-2 border-black rounded-xl px-4 py-4 text-center"
              data-testid="programme-complete-banner"
            >
              <p className="text-base font-bold text-black">
                The programme is complete. Your Recovery continues.
              </p>
            </div>
          )}

          {/* User Manual prompt: discreet first-time onboarding cue, ONLY during the 2-week window */}
          {effectivelyInTwoWeekWindow && (
            <button
              onClick={() => navigate('/user-manual')}
              className="w-full bg-white hover:bg-zinc-50 border-2 border-black rounded-xl px-3 py-2 mb-3 flex items-center gap-2 text-left transition-colors"
              data-testid="user-manual-prompt"
            >
              <BookOpen className="w-4 h-4 text-black flex-shrink-0" />
              <span className="text-base text-black flex-1 leading-tight">
                <strong className="text-black">New here?</strong> The User Manual shows where every section lives: including <em>Relapse Recovery</em>.
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-black flex-shrink-0" />
            </button>
          )}

          {/* Unified Box: Sobriety Clock + Date + Progress + Badges */}
          <div className="bg-white border-2 border-black rounded-xl p-4 mb-6" data-testid="progress-badges-box">
            <SobrietyClock sobrietyDate={user?.sobriety_date} sobrietyTime={user?.sobriety_time} primaryDrugOfChoice={user?.primary_drug_of_choice} inline={true} />
            {/* Footer row: Date + Progress + Badges */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t-2 border-black">
              {sobrietyDateFormatted ? (
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-black flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-black leading-none">Last drink or drug</p>
                    <p className="text-sm font-semibold text-black truncate" data-testid="sobriety-date-display">{sobrietyDateFormatted}</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-black leading-none">Programme</p>
                  <p className="text-sm font-semibold text-black">{pct}% complete</p>
                </div>
              )}
              <div className="w-px h-8 bg-black flex-shrink-0" />
              <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="programme-progress-indicator">
                <div className="relative w-10 h-10">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="20" cy="20" r="16" stroke="rgba(0,0,0,0.15)" strokeWidth="3" fill="none" />
                    <circle cx="20" cy="20" r="16" stroke="#000000" strokeWidth="3" fill="none"
                      strokeDasharray={`${2 * Math.PI * 16}`}
                      strokeDashoffset={`${2 * Math.PI * 16 * (1 - pct / 100)}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-black">{pct}%</span>
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-bold text-black">Programme</p>
                  <p className="text-sm font-semibold text-black">progress</p>
                </div>
              </div>
              <div className="w-px h-8 bg-black flex-shrink-0" />
              <button onClick={() => navigate('/badges')} className="flex items-center gap-1.5 flex-shrink-0 hover:bg-zinc-100 rounded-lg px-1.5 py-1 transition-colors" data-testid="view-all-badges">
                <Award className="w-3.5 h-3.5 text-black" />
                {badgesLoading ? (
                  <span className="inline-block w-3 h-3 rounded bg-black/20 animate-pulse" aria-hidden="true" data-testid="badge-count-skeleton" />
                ) : (
                  <span className="text-sm font-bold text-black">{earnedBadges?.length || 0}</span>
                )}
                <div className="leading-tight text-left">
                  <p className="text-sm font-bold text-black">Badges</p>
                  <p className="text-sm font-semibold text-black">earned</p>
                </div>
                <ChevronRight className="w-3 h-3 text-black" />
              </button>
            </div>
          </div>

          {/* Assessment & Profile card: moved to sit below the Clean Clock
              and Thought of the Day per user request. Collapsible dropdown. */}
          <div
            className="bg-white border-2 border-black rounded-xl p-4 mb-4"
            data-testid="assessment-profile-card"
          >
            <button
              type="button"
              onClick={() => setAssessmentOpen((v) => !v)}
              className="w-full flex items-center gap-2 text-left"
              aria-expanded={assessmentOpen}
              aria-controls="assessment-profile-items"
              data-testid="assessment-profile-toggle"
            >
              <div className="w-8 h-8 rounded-full bg-white border-2 border-black flex items-center justify-center">
                <User className="w-4 h-4 text-black" />
              </div>
              <div className="flex-1">
                <h3 className="text-black font-bold text-base">Assessment and profile</h3>
                <p className="text-black text-base">Your history, profile and case management</p>
              </div>
              {personalHistoryStatus.completed && (
                <span
                  className="text-sm font-semibold bg-white text-black border-2 border-black rounded-full px-2 py-0.5"
                  data-testid="assessment-complete-badge"
                >
                  Complete
                </span>
              )}
              {assessmentOpen
                ? <ChevronUp className="w-4 h-4 text-black flex-shrink-0" />
                : <ChevronDown className="w-4 h-4 text-black flex-shrink-0" />}
            </button>

            {assessmentOpen && (
              <div id="assessment-profile-items" className="space-y-2 mt-3">
              {/* Personal History / Admission Assessment */}
              <button
                onClick={() => navigate('/personal-history', {
                  state: { incompleteSections: personalHistoryStatus.missing_sections || [] }
                })}
                className="w-full flex items-center gap-3 bg-white hover:bg-zinc-50 border-2 border-black rounded-lg px-3 py-2.5 text-left transition-colors group"
                data-testid="dashboard-personal-history-link"
              >
                <ClipboardList className="w-4 h-4 text-black flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-black text-base font-semibold">Personal History</div>
                  <div className="text-black text-base">
                    {personalHistoryStatus.completed
                      ? 'Review or update your admission assessment'
                      : personalHistoryStatus.progress > 0
                        ? `Continue: ${personalHistoryStatus.progress}% complete`
                        : 'Start your admission assessment'}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
              </button>

              {/* View Profile */}
              <button
                onClick={() => navigate('/profile')}
                className="w-full flex items-center gap-3 bg-white hover:bg-zinc-50 border-2 border-black rounded-lg px-3 py-2.5 text-left transition-colors group"
                data-testid="dashboard-profile-link"
              >
                <User className="w-4 h-4 text-black flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-black text-base font-semibold">View Profile</div>
                  <div className="text-black text-base">Avatar, sobriety details and account info</div>
                </div>
                <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
              </button>

              {/* Case Management */}
              <button
                onClick={() => navigate('/case-management')}
                className="w-full flex items-center gap-3 bg-white hover:bg-zinc-50 border-2 border-black rounded-lg px-3 py-2.5 text-left transition-colors group"
                data-testid="dashboard-case-management-link"
              >
                <Folder className="w-4 h-4 text-black flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-black text-base font-semibold">Case Management</div>
                  <div className="text-black text-base">Court documents, sponsor and support team</div>
                </div>
                <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
              </button>
              </div>
            )}
          </div>

          {/* Task Dropdowns */}
          <div className="space-y-4">
            <DailyTasksDropdown
              journalCompleted={progress?.daily_journal || false}
              personalHistoryCompleted={personalHistoryStatus.completed}
              personalHistoryProgress={personalHistoryStatus.progress}
              personalHistoryMissingSections={personalHistoryStatus.missing_sections || []}
              isInTwoWeekWindow={effectivelyInTwoWeekWindow}
              notifications={notifications.filter(n => !n.read)}
            />
            <WeeklyTasksDropdown
              meetingsLogged={progress?.weekly_meetings || 0}
              meetingsRequired={progress?.weekly_meetings_required || 5}
              readingsCompleted={false}
              selfWorkCompleted={selfWorkStatus.done}
              selfWorkProgress={selfWorkStatus.progress}
              selfWorkCompletedCount={selfWorkStatus.completed}
              selfWorkTotal={selfWorkStatus.total}
              currentWeek={user?.current_week || -2}
              meetingNotifications={notifications.filter(n => n.type === 'meetingReminder' || n.type === 'meeting')}
              dietPrefsCompleted={dietPrefsCompleted}
              weeklyMentalHealth={progress?.weekly_mental_health || 0}
              weeklyMentalHealthRequired={progress?.weekly_mental_health_required || 2}
              swReminder={swReminder}
            />
          </div>

          {/* (Self-Work reminder card moved into the Weekly Tasks dropdown.) */}

          {/* Tonight's Idea: subtle Diet widget */}
          {tonightIdea && (
            <button
              onClick={() => navigate(`/library?tab=diet&meal=${tonightIdea.id}`)}
              className="w-full mt-4 bg-white border-2 border-black rounded-xl px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
              data-testid="tonights-idea-widget"
            >
              <div className="w-8 h-8 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0 text-base">🍽️</div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-black">Tonight's idea</div>
                <div className="text-base text-black truncate">{localizedTitle(tonightIdea, language)}</div>
              </div>
              <span className="text-base text-black">Open recipe →</span>
            </button>
          )}

          {/* Recovery Rooms: single collapsible dropdown listing only eligible rooms */}
          <div className="mt-4 bg-white border-2 border-black rounded-xl overflow-hidden" data-testid="recovery-rooms-dropdown">
            <button
              onClick={() => canAccessChat && setRoomsExpanded(v => !v)}
              disabled={!canAccessChat}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${canAccessChat ? 'hover:bg-zinc-50' : 'opacity-90 cursor-default'}`}
              data-testid="recovery-rooms-toggle"
            >
              <div className="w-9 h-9 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0 relative">
                <MessageCircle className="w-5 h-5 text-black" />
                {canAccessChat && chatNotifCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center border-2 border-white"
                    data-testid="chat-notif-badge"
                    title={`${chatNotifCount} pending: ${chatNotif.pending_favourites || 0} favourite requests, ${chatNotif.unread_dms || 0} unread DMs`}
                  >
                    {chatNotifCount > 9 ? '9+' : chatNotifCount}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-black">Recovery Rooms</h3>
                {canAccessChat ? (
                  <p className="text-base text-black">Peer support · choose a room</p>
                ) : (
                  <p className="text-base text-black">Available after your 2-Week Window assessment</p>
                )}
              </div>
              {canAccessChat && (
                roomsExpanded
                  ? <ChevronUp className="w-4 h-4 text-black flex-shrink-0" />
                  : <ChevronDown className="w-4 h-4 text-black flex-shrink-0" />
              )}
            </button>

            {canAccessChat && roomsExpanded && (
              <div className="border-t-2 border-black divide-y-2 divide-black">
                <button
                  onClick={() => navigate('/group-chat')}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                  data-testid="rooms-item-main"
                >
                  <div className="w-7 h-7 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-black" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-black">Recovery Room (RR)</p>
                    <p className="text-base text-black">Country and Global · auto-translated</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
                </button>

                {(user?.gender === 'female' || isSuperAdmin) && (
                  <button
                    onClick={() => navigate('/women-only')}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                    data-testid="rooms-item-women"
                  >
                    <div className="w-7 h-7 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0">
                      <Heart className="w-4 h-4 text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-black">Women-only safe space</p>
                      <p className="text-base text-black">Same-gender peer support and sponsorship.</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
                  </button>
                )}

                {(user?.gender === 'male' || isSuperAdmin) && (
                  <button
                    onClick={() => navigate('/men-only')}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                    data-testid="rooms-item-men"
                  >
                    <div className="w-7 h-7 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-4 h-4 text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-black">Men-only safe space</p>
                      <p className="text-base text-black">Man-to-man accountability and support.</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
                  </button>
                )}

                {(isLgbtqi || isSuperAdmin) && (
                  <button
                    onClick={() => navigate('/group-chat', { state: { room: 'LGBTQI+' } })}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                    data-testid="rooms-item-lgbtqi"
                  >
                    <div className="w-7 h-7 rounded-lg bg-white border-2 border-black flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-black">LGBTQI+ Recovery Room</p>
                      <p className="text-base text-black">A safe space for the LGBTQI+ community.</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-black flex-shrink-0" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Relapse Recovery and Take a Break moved into the user profile
              avatar dropdown.  Removed from the dashboard surface to reduce
              visual clutter. */}

          {/* Chat with Bot */}
          <Button
            onClick={() => navigate('/bot')}
            className="w-full mt-4 rounded-xl h-14 px-6 font-bold text-base border-2 border-black text-white hover:brightness-95"
            style={{ backgroundColor: '#0057e7' }}
            data-testid="chat-with-bot-btn"
            data-arp="btn-primary"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Chat with {user?.bot_name || 'Tudor'}
          </Button>
        </div>
      </main>

      {/* Bottom Menu */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-black z-40 h-16">
        <div className="flex items-center justify-center gap-1 sm:gap-2 h-full max-w-sm mx-auto px-1">
          <button onClick={() => navigate('/dashboard')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 bg-zinc-100 rounded-lg" data-testid="dashboard-nav-btn">
            <div className="w-1.5 h-1.5 bg-black rounded-full mb-0.5" />
            <span className="text-sm font-bold text-black">{t('nav.dashboard')}</span>
          </button>
          <button onClick={() => navigate('/library?tab=resources')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="library-nav-btn" aria-label="Resources">
            <BookOpen className="w-5 h-5 text-black" strokeWidth={2} />
            <span className="text-sm font-medium text-black">{t('nav.library')}</span>
          </button>
          {user?.role === 'superadmin' && (
            <button onClick={() => navigate('/inbox')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="inbox-nav-btn">
              <ShieldAlert className="w-5 h-5 text-black" strokeWidth={2} />
              <span className="text-sm font-medium text-black">{t('nav.inbox')}</span>
            </button>
          )}
          <button onClick={() => navigate('/group-chat')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="group-chat-nav-btn" aria-label="Recovery Room">
            <Users className="w-5 h-5 text-black" strokeWidth={2} />
            <span className="text-sm font-medium text-black">RR</span>
          </button>
          <button onClick={() => navigate('/journey')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="journey-nav-btn">
            <Menu className="w-5 h-5 text-black" strokeWidth={2} />
            <span className="text-sm font-medium text-black">{t('nav.journey')}</span>
          </button>
          {/* Sep 2026 Tudor Fix 2: HELP must be reachable in one tap on every
              page, including Dashboard, without being covered by the shared
              Footer.js. Adding it here makes the touch target hittable. */}
          <button onClick={() => navigate('/get-help')} className="flex flex-col items-center justify-center space-y-1 px-2 py-2 hover:bg-red-50 rounded-lg transition-colors" data-testid="dashboard-help-nav-btn">
            <LifeBuoy className="w-5 h-5 text-red-600" strokeWidth={2} />
            <span className="text-sm font-medium text-red-600">Help</span>
          </button>
        </div>
      </footer>

      {/* Quick check-in modal: appears only after the card is opened.
          Answers stay client-side; only a single anonymous event is sent
          on submit.  See /app/backend/routes/quick_checkin.py. */}
      <QuickCheckinModal
        open={checkinOpen}
        onClose={() => { setCheckinOpen(false); refreshCheckinDue(); }}
        onCompleted={() => { hideCheckinCard(); }}
      />
    </div>
  );
}

export default DashboardPage;
